import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Copy, Phone, User, Clock, CheckCircle2, AlertCircle, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function WhatsAppPage() {
  const [waConfig, setWaConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/whatsapp/config'),
      API.get('/whatsapp/sessions')
    ]).then(([cfg, sess]) => {
      setWaConfig(cfg.data);
      setSessions(sess.data.sessions || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyWebhook = () => {
    if (waConfig?.webhook_url) {
      navigator.clipboard.writeText(waConfig.webhook_url);
      toast.success('Webhook URL copied');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[1200px]">
      <div>
        <h1 data-testid="whatsapp-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>WhatsApp Integration</h1>
        <p className="text-slate-500 mt-1">Connect the AI triage bot to WhatsApp for patient communication</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
              <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Connection Status</CardTitle></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              {waConfig?.configured ? (
                <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Connected</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3.5 h-3.5 mr-1" /> Not Configured</Badge>
              )}
            </div>
            {waConfig?.twilio_phone && <p className="text-sm text-slate-600">WhatsApp Number: <strong>{waConfig.twilio_phone}</strong></p>}
            {!waConfig?.configured && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">To enable WhatsApp, configure your Twilio credentials in <strong>Settings &gt; Communication</strong> tab.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Webhook Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">Set this URL as your Twilio WhatsApp webhook (HTTP POST) in your Twilio Console:</p>
            <div className="flex gap-2">
              <Input value={waConfig?.webhook_url || ''} readOnly className="font-mono text-xs flex-1" data-testid="wa-webhook-url" />
              <Button variant="outline" onClick={copyWebhook} data-testid="copy-webhook"><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 text-xs text-slate-500">
              <p className="font-medium text-slate-700">Setup Steps:</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Go to Twilio Console &gt; Messaging &gt; WhatsApp Senders</li>
                <li>Configure your WhatsApp Business number</li>
                <li>Set the webhook URL above for "When a message comes in"</li>
                <li>Method: HTTP POST</li>
              </ol>
            </div>
            <div className="p-3 bg-sky-50 rounded-lg border border-sky-100">
              <p className="text-xs text-sky-800"><strong>How it works:</strong> When a patient sends a WhatsApp message, the system checks if they're a known patient (by phone number). If yes, it welcomes them by name. If not, it collects their full name, gender, and date of birth before starting the AI triage conversation.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>WhatsApp Conversations</CardTitle>
            <Badge variant="secondary">{sessions.length} sessions</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {sessions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No WhatsApp conversations yet</p>
              <p className="text-xs mt-1">Conversations will appear here once patients message your WhatsApp number</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Patient</TableHead>
                  <TableHead className="font-semibold text-slate-700">Phone</TableHead>
                  <TableHead className="font-semibold text-slate-700">Status</TableHead>
                  <TableHead className="font-semibold text-slate-700">Messages</TableHead>
                  <TableHead className="font-semibold text-slate-700">Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map(s => (
                  <TableRow key={s.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"><User className="w-4 h-4 text-emerald-600" /></div>
                        <span className="font-medium text-slate-900">{s.patient_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{s.phone}</TableCell>
                    <TableCell>
                      <Badge className={s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : s.status === 'in_triage' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}>
                        {s.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{(s.messages || []).length}</TableCell>
                    <TableCell className="text-sm text-slate-500">{new Date(s.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
